/* eslint-disable react/jsx-one-expression-per-line */
import React, { useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';

import CustomCheckbox from '../../../../../../generic/CustomCheckbox';
import CustomInput from '../../../../../../generic/CustomInput';
import CustomTextarea from '../../../../../../generic/CustomTextarea';
import CustomBtn from '../../../../../../generic/CustomBtn';
import { CustomReactSelect as CustomSelect } from '../../../../../../generic/CustomSelect/CustomSelect';
import Tooltip from '../../../../../../components/Tooltip';
import TreeDensityPopup from '../TreeDensityPopup';
import './GeneralInformation.scss';

import tooltipIconSrc, { ReactComponent as TooltipIcon } from '../../../../../../assets/image/carbon/circle-exclamation.svg';
import NotificationAlert from '../../../../../../generic/NotificationAlert';

const GeneralInformation = ({ defaultValues, tabKey, isActiveTab, disabled }) => {
  const translate = useIntl();
  const { register, errors, control, setValue } = useFormContext();
  const [isTreeDensityPopup, setIsTreeDensityPopup] = useState(false);
  const [statusAlert, setStatusAlert] = useState({ years: false, area: false });
  const { [tabKey]: formErrors } = errors;
  const changeStatusAlert = (type, value) => {
    if (type === 'years') {
      const isWarning = +value < 40;
      return setStatusAlert({ ...statusAlert, [type]: isWarning });
    }
    const isWarning = value.length < 150;

    setStatusAlert({ ...statusAlert, [type]: isWarning });
  };
  const rangeNumbersOptions = useMemo(() => (
    Array.from({ length: 50 }).map((_, index) => ({
      value: `${index + 1}`,
      label: `${index + 1}`,
    }))
  ), []);

  const yearsOptions = useMemo(() => {
    const endYear = new Date().getFullYear() + 5;
    const startYear = 2010;
    const length = endYear - startYear + 1;

    return Array.from({ length }).map((_, index) => ({
      value: `${startYear + index}`,
      label: `${startYear + index}`,
    }));
  }, []);

  const continentOptions = useMemo(() => ([
    { value: 'africa', label: translate.formatMessage({ id: 'select.option.africa' }) }, { value: 'eurasia', label: translate.formatMessage({ id: 'select.option.eurasia' }) },
    { value: 'asia-oceania', label: translate.formatMessage({ id: 'select.option.asiaOceania' }) }, { value: 'continental', label: translate.formatMessage({ id: 'select.option.continental' }) },
    { value: 'insular', label: translate.formatMessage({ id: 'select.option.insular' }) }, { value: 'america', label: translate.formatMessage({ id: 'select.option.america' }) },
  ]), []);

  const numbersOptions = useMemo(() => (
    Array.from({ length: 100 }).map((_, index) => ({
      value: `${index + 1}`,
      label: `${index + 1}`,
    }))
  ), []);

  const forestTypeOptions = useMemo(() => ([
    { value: 'tropical-dry', label: translate.formatMessage({ id: 'select.option.tropicalDry' }) },
    { value: 'tropical-moist', label: translate.formatMessage({ id: 'select.option.tropicalMoist' }) },
    { value: 'tropical-wet', label: translate.formatMessage({ id: 'select.option.tropicalWet' }) },
    { value: 'subtropical-dry', label: translate.formatMessage({ id: 'select.option.subtropicalDry' }) },
    { value: 'subtropical-wet', label: translate.formatMessage({ id: 'select.option.subtropicalWet' }) },
    { value: 'temperate-coniferous', label: translate.formatMessage({ id: 'select.option.temperateConiferous' }) },
    { value: 'temperate-broadleaf', label: translate.formatMessage({ id: 'select.option.temperateBroadleaf' }) },
    { value: 'boreal-forest-tundra', label: translate.formatMessage({ id: 'select.option.borealForestTundra' }) },
    { value: 'boreal-mixed-coniferous-broadleaf', label: translate.formatMessage({ id: 'select.option.borealMixedConiferousBroadleaf' }) },
    { value: 'boreal-coniferous', label: translate.formatMessage({ id: 'select.option.borealConiferous' }) },
    { value: 'boreal-broadleaf', label: translate.formatMessage({ id: 'select.option.borealBroadleaf' }) },
  ]), []);

  const forestTypeTooltipBody = (
    <ul className="forest-type-tooltip">
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.tropicalDry"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.tropicalMoist"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.tropicalWet"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.subtropicalDry"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.subtropicalWet"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.temperate"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <FormattedMessage
        id="carbon.form.forestTypeTooltip.boreal"
        values={{
          li: (...chunks) => <li>{chunks}</li>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    </ul>
  );

  const getTooltip = (body = '', id = '') => (
    <Tooltip
      wrapperClass="tooltip"
      targetElement={<TooltipIcon data-tip data-iscapture data-for={id} />}
      tooltipElement={body}
      tooltipProps={{ className: 'tooltip__message', id }}
    />
  );

  const isTrueString = (s) => s === 'true';
  const isTropicalOrSubtropical = (s) => s.includes('tropical');
  const isPines = (s) => s === 'pines';
  const watchIsGhgCarbon = useWatch({ name: `${tabKey}.isGhgCarbon`, exact: true });
  const watchForestType = useWatch({ name: `${tabKey}.forestType`, exact: true });

  return (
    <div className="general-info__wrapper" style={{ display: isActiveTab ? 'block' : 'none' }}>
      {isTreeDensityPopup && <TreeDensityPopup closeHandler={() => setIsTreeDensityPopup(false)} />}
      <div className="general-info">
        <div className="general-info__subtitle">
          {translate.formatMessage({ id: 'carbon.genInfo.subtitle' })}
        </div>

        <div className="general-info__item">
          <CustomSelect
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.numberOfYears' })}
            labelText={translate.formatMessage({ id: 'carbon.form.yearsInvolved' })}
            options={rangeNumbersOptions}
            inputClass="select-input"
            titleClass="form-field-title"
            name={`${tabKey}.yearsInvolved`}
            defaultValue={defaultValues.yearsInvolved}
            control={control}
            rules={{ required: true }}
            error={formErrors?.yearsInvolved}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <div>
            <p className="form-field-title">{translate.formatMessage({ id: 'carbon.form.isGhgCarbon' })}</p>
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.isGhgCarbon`}
                defaultChecked={isTrueString(defaultValues.isGhgCarbon)}
                register={register({ required: true })}
                error={formErrors?.isGhgCarbon}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.isGhgCarbon`}
                defaultChecked={isTrueString(defaultValues.isGhgCarbon)}
                register={register({ required: true })}
                error={formErrors?.isGhgCarbon}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="general-info__item">
          <CustomInput
            type="text"
            label={translate.formatMessage({ id: 'carbon.form.ghgCarbonPrograms' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            customClass="input-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.ghgCarbonPrograms`}
            value={defaultValues.ghgCarbonPrograms}
            register={register({ required: isTrueString(watchIsGhgCarbon) })}
            error={formErrors?.ghgCarbonPrograms}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomSelect
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.continent' })}
            labelText={translate.formatMessage({ id: 'carbon.form.continent' })}
            options={continentOptions}
            inputClass="select-input"
            titleClass="form-field-title"
            name={`${tabKey}.continent`}
            defaultValue={defaultValues.continent}
            control={control}
            rules={{ required: true }}
            error={formErrors?.continent}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomInput
            type="text"
            label={translate.formatMessage({ id: 'carbon.form.country' })}
            customClass="input-field input-field_short"
            customClassLabel="form-field-title"
            name={`${tabKey}.country`}
            value={defaultValues.country}
            register={register()}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomSelect
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.years' })}
            labelText={translate.formatMessage({ id: 'carbon.form.startPlantingActivities' })}
            options={yearsOptions}
            inputClass="select-input"
            titleClass="form-field-title"
            name={`${tabKey}.startPlantingActivities`}
            defaultValue={defaultValues.startPlantingActivities}
            control={control}
            rules={{ required: true }}
            error={formErrors?.startPlantingActivities}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomSelect
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.numberOfYears' })}
            labelText={translate.formatMessage({ id: 'carbon.form.durationForPlanting' })}
            options={numbersOptions}
            inputClass={`select-input${statusAlert.years ? ' warning' : ''}`}
            titleClass="form-field-title"
            name={`${tabKey}.durationForPlanting`}
            defaultValue={defaultValues.durationForPlanting}
            control={control}
            rules={{ required: true }}
            selectOnChange={(e) => {
              changeStatusAlert('years', e);
            }}
            error={formErrors?.durationForPlanting}
            disabled={disabled}
          />
          {statusAlert.years && <NotificationAlert text={translate.formatMessage({ id: 'carbon.form.durationForPlanting.warning' })} />}
        </div>
        <div className="general-info__item">
          <CustomInput
            type="text"
            label={translate.formatMessage({ id: 'carbon.form.projectSize' })}
            customClass="input-field input-field_short"
            customClassLabel="form-field-title"
            name={`${tabKey}.projectSize`}
            value={defaultValues.projectSize}
            register={register()}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.communitiesAndStakeholders' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.communitiesAndStakeholders`}
            value={defaultValues.communitiesAndStakeholders}
            register={register({ required: true })}
            error={formErrors?.communitiesAndStakeholders}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomSelect
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.projectForestType' })}
            labelText={translate.formatMessage({ id: 'carbon.form.forestType' })}
            options={forestTypeOptions}
            inputClass="select-input"
            titleClass="form-field-title"
            tooltip={getTooltip(forestTypeTooltipBody, 'forest-type')}
            name={`${tabKey}.forestType`}
            defaultValue={defaultValues.forestType}
            control={control}
            rules={{ required: true }}
            error={formErrors?.forestType}
            selectOnChange={(value) => {
              if (!value || !isTropicalOrSubtropical(value)) {
                setValue(`${tabKey}.subOrTropicalTrees`, '');
              }
            }}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <div>
            <p className="form-field-title">{translate.formatMessage({ id: 'carbon.form.subOrTropicalTrees' })}</p>
            <div>
              <CustomCheckbox
                type="radio"
                value="pines"
                title={translate.formatMessage({ id: 'carbon.form.pines' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.subOrTropicalTrees`}
                defaultChecked={isPines(defaultValues.subOrTropicalTrees)}
                register={register({ required: isTropicalOrSubtropical(watchForestType?.value || '') })}
                error={formErrors?.subOrTropicalTrees}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="broadleaf"
                title={translate.formatMessage({ id: 'carbon.form.broadleaf' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.subOrTropicalTrees`}
                defaultChecked={isPines(defaultValues.subOrTropicalTrees)}
                register={register({ required: isTropicalOrSubtropical(watchForestType?.value || '') })}
                error={formErrors?.subOrTropicalTrees}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="general-info__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.mainTypeOfActivity' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.mainTypeOfActivity`}
            value={defaultValues.mainTypeOfActivity}
            register={register({ required: true })}
            error={formErrors?.mainTypeOfActivity}
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <CustomInput
            type="text"
            label={translate.formatMessage({ id: 'carbon.form.treeDensity' })}
            customClass="input-field input-field_short"
            customClassLabel="form-field-title"
            name={`${tabKey}.treeDensity`}
            value={defaultValues.treeDensity}
            register={register()}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className="general-info__item">
          <div>
            <div className="form-field-title">
              <span>{translate.formatMessage({ id: 'carbon.form.isRelativelyHomogeneous' })}</span>
              <CustomBtn
                handleClick={() => setIsTreeDensityPopup(true)}
                iconSvg={tooltipIconSrc}
                customClass="tooltip-btn"
              />
            </div>
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.isRelativelyHomogeneous`}
                defaultChecked={isTrueString(defaultValues.isRelativelyHomogeneous)}
                register={register({ required: true })}
                error={formErrors?.isRelativelyHomogeneous}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.isRelativelyHomogeneous`}
                defaultChecked={isTrueString(defaultValues.isRelativelyHomogeneous)}
                register={register({ required: true })}
                error={formErrors?.isRelativelyHomogeneous}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="general-info__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.positiveImpactsDescription' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea={`textarea-field${statusAlert.area ? ' warning' : ''}`}
            customClassLabel="form-field-title"
            name={`${tabKey}.positiveImpactsDescription`}
            value={defaultValues.positiveImpactsDescription}
            register={register({ required: true })}
            error={formErrors?.positiveImpactsDescription}
            disabled={disabled}
            change={(e) => {
              changeStatusAlert('area', e.target.value);
            }}
          />
          {statusAlert.area && <NotificationAlert text={translate.formatMessage({ id: 'carbon.form.positiveImpactsDescription.warning' })} />}

        </div>
      </div>
    </div>
  );
};

export default GeneralInformation;
